import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Database, Boxes, GraduationCap, Code2, Globe, Layers, MessageSquarePlus } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import Reveal from "../components/Reveal";
import InquiryModal from "../components/InquiryModal";
import { Toaster } from "sonner";

const ICONS = { Sparkles, Database, Boxes, GraduationCap, Code2, Globe };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeInquiry, setActiveInquiry] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="products-page" className="pt-32 pb-20 relative">
      <Toaster position="top-center" theme="dark" />
      <div className="absolute inset-0 -z-10 mesh-bg opacity-90" />
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="orb orb-flame w-[500px] h-[500px] -top-32 -left-32 opacity-30" />
        <div className="orb orb-azure w-[600px] h-[600px] top-1/2 -right-40 opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Hero */}
        <Reveal>
          <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.25em] text-[#FF6B1A] uppercase">
            <Layers size={14} /> Our Products
          </span>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-4 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05]">
            Software products built<br />by Codeflare, <span className="fb-text">used by industry</span>.
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-7 max-w-2xl text-xl text-zinc-300 leading-relaxed">
            Battle-tested SaaS that powers our clients today — and now available to power yours.
          </p>
        </Reveal>

        {loading ? (
          <p className="text-zinc-500 mt-12">Loading products...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            {products.map((p, i) => {
              const Icon = ICONS[p.icon] || Sparkles;
              const accent = p.accent || "#FF6B1A";
              return (
                <Reveal key={p.id} delay={i * 0.07}>
                  <div className="block group" data-testid={`product-card-${p.slug}`}>
                    <motion.div
                      whileHover={{ y: -8 }}
                      className="tilt-card glass rounded-3xl overflow-hidden hover:border-[#FF6B1A]/40 transition-all spotlight relative"
                      onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty('--x', `${e.clientX - r.left}px`); e.currentTarget.style.setProperty('--y', `${e.clientY - r.top}px`); }}
                    >
                      <Link to={`/products/${p.slug}`} className="block">
                        <div className="aspect-[16/9] relative overflow-hidden">
                          {p.hero_image && <img src={resolveImageUrl(p.hero_image)} alt={p.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-110 transition-all duration-700" />}
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                          <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 80% 30%, ${accent}30, transparent 60%)` }} />
                          <div className="absolute top-5 left-5 flex items-center gap-2">
                            <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}33, transparent)`, border: `1px solid ${accent}66` }}>
                              <Icon size={18} style={{ color: accent }} />
                            </span>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-white/80 px-2 py-1 rounded-full bg-black/60 border border-white/10">{p.category}</span>
                          </div>
                        </div>
                      </Link>
                      <div className="p-7">
                        <Link to={`/products/${p.slug}`}>
                          <h3 className="text-2xl font-bold flex items-center gap-2">
                            {p.name}
                            <ArrowRight size={18} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" style={{ color: accent }} />
                          </h3>
                          <p className="text-zinc-400 text-sm mt-2.5 leading-relaxed">{p.tagline}</p>
                        </Link>
                        {p.features && p.features.length > 0 && (
                          <ul className="mt-5 space-y-1.5">
                            {p.features.slice(0, 3).map((f) => (
                              <li key={f} className="text-xs text-zinc-300 flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full" style={{ background: accent }} /> {f}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-6 flex items-center gap-3">
                          <button
                            onClick={() => setActiveInquiry({ key: p.slug, label: p.name, accent })}
                            data-testid={`product-inquiry-btn-${p.slug}`}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-black"
                            style={{ background: `linear-gradient(135deg, ${accent}, #FFAA33)` }}
                          >
                            <MessageSquarePlus size={14} /> {p.cta_text || "Book demo"}
                          </button>
                          <Link to={`/products/${p.slug}`} className="text-sm font-medium" style={{ color: accent }}>
                            Learn more →
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        <Reveal>
          <div className="mt-24 gradient-border">
            <div className="rounded-3xl p-10 text-center">
              <Sparkles className="mx-auto text-[#FFAA33]" size={32} />
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold">Need something custom?</h2>
              <p className="text-zinc-400 mt-3 max-w-xl mx-auto">We can fork, white-label or extend any of our products to fit your domain.</p>
              <Link to="/contact" className="inline-block mt-6 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] via-[#FFAA33] to-[#3B82F6] text-black font-semibold">Talk to us</Link>
            </div>
          </div>
        </Reveal>
      </div>

      <InquiryModal
        open={!!activeInquiry}
        onClose={() => setActiveInquiry(null)}
        type="product"
        itemKey={activeInquiry?.key}
        itemLabel={activeInquiry?.label}
        accent={activeInquiry?.accent}
      />
    </div>
  );
}
